'use client';

import { useState, useEffect, useRef } from 'react';
import { LayoutContainer, LoadingIndicator } from '@petsmart-ui/sparky';

import './ProductReview.scss';
import {
    Events,
    PageSectionAnalyticsTracking,
    dataLayerPush,
} from '@libs/analytics';

type ProductReviewProps = {
    productId?: number;
};

export default function ProductReview({ productId }: ProductReviewProps) {
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const reviewRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                    }
                });
            },
            {
                root: null,
                rootMargin: '200px', // It is activated 200px before entering the screen
                threshold: 0.1,
            }
        );

        if (reviewRef.current) {
            observer.observe(reviewRef.current);
        }

        return () => {
            if (reviewRef.current) {
                observer.disconnect();
            }
        };
    }, []);

    return (
        <div ref={reviewRef}>
            {isLoading && (
                <LoadingIndicator className="product-review__loading-indicator" />
            )}

            {isVisible && productId && (
                <LayoutContainer
                    className="product-review__container"
                    data-testid="product-review-layout"
                >
                    <PageSectionAnalyticsTracking
                        sectionId="bv-reviews"
                        eventCallback={() => {
                            dataLayerPush({
                                event: Events.VIEW_PAGE_SECTION,
                                page_section: 'reviews',
                            });
                        }}
                        OnIntersect={() => setIsLoading(false)}
                    >
                        <div
                            data-bv-show="reviews"
                            data-bv-product-id={productId}
                        />
                    </PageSectionAnalyticsTracking>
                    <PageSectionAnalyticsTracking
                        sectionId="bv-questions"
                        eventCallback={() => {
                            dataLayerPush({
                                event: Events.VIEW_PAGE_SECTION,
                                page_section: 'questions',
                            });
                        }}
                    >
                        <div
                            data-bv-show="questions"
                            data-bv-product-id={productId}
                        />
                    </PageSectionAnalyticsTracking>
                </LayoutContainer>
            )}
        </div>
    );
}
